import * as React from 'react';
import { useRef, useState, useEffect, useCallback } from 'react';
import { calculatePrecision, preventNonNumberKey, roundToPrecision } from './utils';

const canUseDOM = typeof window !== 'undefined';

export interface UseNumberInputProps {
  /**
   * The value of the input. Should be less than `max` and greater than `min`
   */
  value?: string | number;
  /**
   * The initial value of the input. Should be less than `max` and greater than `min`
   */
  defaultValue?: string | number;
  /**
   * The callback fired when the value changes
   * @param {Number} value - The next value
   */
  onChange?: (value: number | string) => void;
  onDecrement?: (value: number | string) => void;
  onIncrement?: (value: number | string) => void;
  onBlur?: () => void;
  /**
   * If `true`, the input will be focused as you increment
   * or decrement the value with the stepper
   *
   * @default true
   */
  focusInputOnChange?: boolean;
  /**
   * This controls the value update when you blur out of the input.
   * - If `true` and the value is greater than `max`, the value will be reset to `max`
   * - Else, the value remains the same.
   *
   * @default true
   */
  clampValueOnBlur?: boolean;
  /**
   * This controls the value update behavior in general.
   * - If `true` and you use the stepper or up/down arrow keys,
   *  the value will not exceed the `max` or go lower than `min`
   * - Else, the value will be allowed to go out of range.
   *
   * @default true
   */
  keepWithinRange?: boolean;
  /**
   * The minimum value of the input
   * @default -Infinity
   */
  min?: number;
  /**
   * The maximum value of the input
   * @default Infinity
   */
  max?: number;
  /**
   * The step used to increment or decrement the value
   * @default 1
   */
  step?: number;
  /**
   * The number of decimal points used to round the value
   */
  precision?: number;
  /**
   * This is used to format the value so that screen readers
   * can speak out a more human-friendly value.
   *
   * It is used to set the `aria-valuetext` property of the input
   *
   * @param {Number} value - the current value
   */
  getAriaValueText?: (value?: string | number) => string;
  /**
   * If `true`, the input will be in readonly mode
   */
  isReadOnly?: boolean;
  /**
   * If `true`, the input will have `aria-invalid` set to `true`
   */
  isInvalid?: boolean;
  /**
   * If `true`, the input will be disabled
   */
  isDisabled?: boolean;
  /**
   * If `true`,
   */
  loading?: boolean;
  setInputValue: (value: number | string | undefined) => void;
  setOnInput: (value: any) => void;
  onInput: number;
}

const noop = () => ({});

function useLongPress(callback: (step?: number) => void = noop, speed = 200) {
  const [isPressed, setIsPressed] = useState(false);

  useEffect(() => {
    let timerId: number | ReturnType<typeof setTimeout> = 0;

    if (isPressed) {
      timerId = setTimeout(callback, speed);
    } else {
      clearTimeout(timerId);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [isPressed, callback, speed]);

  const start = useCallback(() => {
    callback();
    setIsPressed(true);
  }, [callback]);

  const stop = useCallback(() => {
    setIsPressed(false);
  }, []);

  const clickEvent = canUseDOM && !!document.documentElement.ontouchstart ? 'onTouchStart' : 'onMouseDown';

  return {
    [clickEvent]: start,
    onMouseUp: stop,
    onMouseLeave: stop,
    onTouchEnd: stop,
  };
}

function useNumberInput({
  value: valueProp,
  onChange,
  onDecrement,
  onIncrement,
  onBlur: onBlurProps,
  defaultValue,
  focusInputOnChange = true,
  clampValueOnBlur = true,
  keepWithinRange = true,
  min = -Infinity,
  max = Infinity,
  step: stepProp = 1,
  precision: precisionProp,
  getAriaValueText,
  isReadOnly,
  isInvalid,
  isDisabled,
  loading,
  setInputValue,
  setOnInput,
  onInput,
}: UseNumberInputProps): any {
  const { current: isControlled } = useRef(valueProp != null);

  const defaultPrecision = Math.max(calculatePrecision(stepProp), 0);
  const precision = precisionProp || defaultPrecision;

  const [valueState, setValue] = useState(() => {
    if (defaultValue != null) {
      let nextValue = defaultValue;
      if (keepWithinRange) {
        nextValue = Math.max(Math.min(Number(nextValue), max), min);
      }
      nextValue = Number(roundToPrecision(String(nextValue), precision));
      return nextValue;
    }
    return '';
  });

  const [isFocused, setIsFocused] = useState(false);

  const value = isControlled ? valueProp : valueState;
  const isInteractive = !(isReadOnly || isDisabled);
  const inputRef = useRef<any>();

  const prevNextValue = useRef<any>(null);

  const shouldConvertToNumber = (value: string) => {
    if (typeof value !== 'string') return value;

    const hasDot = value.indexOf('.') > -1;
    const hasTrailingZero = value.substr(value.length - 1) === '0';
    const hasTrailingDot = value.substr(value.length - 1) === '.';
    if (hasDot && hasTrailingZero) return false;
    if (hasDot && hasTrailingDot) return false;
    return true;
  };

  const updateValue = (nextValue: string | number) => {
    //eslint-disable-next-line
    if (prevNextValue.current == nextValue) return;

    const shouldConvert = shouldConvertToNumber(String(nextValue));
    const converted = shouldConvert ? +nextValue : nextValue;

    if (!isControlled) setValue(converted);

    if (onChange) onChange(converted);

    prevNextValue.current = nextValue; // save previous value to avoid rendering too many times
  };

  const handleIncrement = (step = stepProp) => {
    if (!isInteractive) return;
    let nextValue = Number(value) + Number(step);

    if (keepWithinRange) {
      nextValue = Math.min(nextValue, max);
    }

    nextValue = Number(roundToPrecision(String(nextValue), precision));
    updateValue(String(nextValue));
    focusInput();
    onIncrement?.(nextValue);
  };

  const handleDecrement = (step = stepProp) => {
    if (!isInteractive) return;
    let nextValue = Number(value) - Number(step);

    if (keepWithinRange) {
      nextValue = Math.max(nextValue, min);
    }

    nextValue = Number(roundToPrecision(String(nextValue), precision));
    updateValue(String(nextValue));
    focusInput();
    onDecrement?.(nextValue);
  };

  const focusInput = () => {
    if (focusInputOnChange && inputRef.current && canUseDOM) {
      requestAnimationFrame(() => {
        inputRef.current.focus();
      });
    }
  };

  const incrementStepperProps = useLongPress(handleIncrement);
  const decrementStepperProps = useLongPress(handleDecrement);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(event.target.value)) || parseInt(event.target.value) < min || parseInt(event.target.value) > max)
      return;
    updateValue(event.target.value);
    setOnInput(!onInput);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    preventNonNumberKey(event);
    if (!isInteractive) return;

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const ratio = getIncrementFactor(event);
      handleIncrement(ratio * stepProp);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const ratio = getIncrementFactor(event);
      handleDecrement(ratio * stepProp);
    }

    if (event.key === 'Home') {
      event.preventDefault();
      if (min != null) {
        updateValue(max);
      }
    }

    if (event.key === 'End') {
      event.preventDefault();
      if (max != null) {
        updateValue(min);
      }
    }
  };

  const getIncrementFactor = (event: React.KeyboardEvent<HTMLInputElement>) => {
    let ratio = 1;
    if (event.metaKey || event.ctrlKey) {
      ratio = 0.1;
    }
    if (event.shiftKey) {
      ratio = 10;
    }
    return ratio;
  };

  const validateAndClamp = () => {
    const maxExists = max != null;
    const minExists = min != null;

    if (maxExists && value && value > max) {
      updateValue(max);
    }

    if (minExists && value && value < min) {
      updateValue(min);
    }
  };

  const isOutOfRange = value && (value > max || value < min);
  const ariaValueText = getAriaValueText ? getAriaValueText(value) : null;

  return {
    value: value,
    isFocused,
    isDisabled,
    isReadOnly,
    incrementStepper: incrementStepperProps,
    decrementStepper: decrementStepperProps,
    incrementButton: {
      onClick: () => handleIncrement(),
      'aria-label': 'add',
      ...(keepWithinRange && {
        disabled: value === max || loading,
        'aria-disabled': value === max,
      }),
    },
    decrementButton: {
      onClick: () => handleDecrement(),
      'aria-label': 'subtract',
      ...(keepWithinRange && {
        disabled: value === min || loading,
        'aria-disabled': value === min,
      }),
    },
    prevNextValue,
    input: {
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      ref: inputRef,
      role: 'spinbutton',
      type: 'text',
      'aria-valuemin': min,
      'aria-valuemax': max,
      'aria-disabled': isDisabled,
      'aria-valuenow': value,
      'aria-invalid': isInvalid || isOutOfRange,
      ...(getAriaValueText && { 'aria-valuetext': ariaValueText }),
      readOnly: isReadOnly,
      disabled: isDisabled,
      autoComplete: 'off',
      onFocus: () => {
        setInputValue('');
        setIsFocused(true);
      },
      onBlur: () => {
        setInputValue(value);
        setIsFocused(false);
        if (clampValueOnBlur) {
          validateAndClamp();
        }
        onBlurProps?.();
      },
    },
    hiddenLabel: {
      'aria-live': 'polite',
      children: getAriaValueText ? ariaValueText : value,
      style: {
        position: 'absolute',
        clip: 'rect(0px, 0px, 0px, 0px)',
        height: 1,
        width: 1,
        margin: -1,
        whiteSpace: 'nowrap',
        border: 0,
        overflow: 'hidden',
        padding: 0,
      },
    },
  };
}

export default useNumberInput;

export * from './utils';
